import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PivotTableChartIcon from "@mui/icons-material/PivotTableChart";
import toast from "react-hot-toast";

import api from "services/api";
import moment from "moment";
import SortableList, { SortableItem } from "react-easy-sort";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import colors from "assets/theme/base/colors";
import parse from "html-react-parser";
import { convert } from "html-to-text";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Slider from "@mui/material/Slider";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import StyleIcon from "@mui/icons-material/Style";
import PaletteIcon from "@mui/icons-material/Palette";

import ImportStylePicturesModal from "pages/de/posts/components/ImportStylePicturesModal";
import ImportMoodboardPicture from "pages/de/posts/components/ImportMoodboardPicture";
import RenderGridContentList from "pages/de/posts/components/RenderGridContentList";
import { getImageSize } from "react-image-size";
import { nanoid } from "nanoid";
import { uploadFileSizeLimit } from "utils/imageHelper";

const DEFAULT_MARGIN_X = 60;

const EditPhotoGrid = React.forwardRef(
  ({ visible, handleClose, handleOk, post, setPost, gridContent }, ref) => {
    const [contentList, setContentList] = useState(gridContent?.content);
    const [gridSetting, setGridSetting] = useState(gridContent);
    const [gridVariantAnchor, setGridVariantAnchor] = useState(null);
    const [importStyleVisible, setImportStyleVisible] = useState(false);
    const [importMoodboardVisible, setImportMoodboardVisible] = useState(false);
    const [uploading, setUploading] = useState(false);

    const uploadRef = useRef(null);

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          contentList,
        }),
        [setContentList]
      );
    }

    const handleSortEnd = (oldIndex, newIndex) => {
      console.log("resort:", oldIndex, newIndex);
      if (contentList.length > 0) {
        var updatedList = [...contentList];
        // Remove dragged item
        const [reorderedItem] = updatedList.splice(oldIndex, 1);
        updatedList.splice(newIndex, 0, reorderedItem);

        setContentList(updatedList);
      }
    };

    const handleSaveNewOrder = () => {
      const newContent = [...post.metadata.content];
      const findIndex = newContent.findIndex((x) => x.id == gridContent.id);

      if (findIndex != -1) {
        const newGridItem = {
          ...gridSetting,
          content: contentList,
        };
        newContent[findIndex] = newGridItem;

        setPost({
          ...post,
          metadata: {
            ...post?.metadata,
            content: newContent,
          },
        });
        handleClose();
      }
    };

    const handleCloseGridVariantMenu = () => setGridVariantAnchor(null);

    const GridVariantMenu = () => (
      <Menu
        id={"grid-variant-menu"}
        anchorEl={gridVariantAnchor}
        open={Boolean(gridVariantAnchor)}
        onClose={handleCloseGridVariantMenu}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={() => {
            uploadRef?.current?.click();
            handleCloseGridVariantMenu();
          }}
        >
          <ListItemIcon>
            <ImageIcon fontSize="medium" />
          </ListItemIcon>
          Image
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportStyleVisible(true);
            handleCloseGridVariantMenu();
          }}
        >
          <ListItemIcon>
            <StyleIcon fontSize="medium" />
          </ListItemIcon>
          Style
        </MenuItem>
        <MenuItem
          onClick={() => {
            setImportMoodboardVisible(true);
            handleCloseGridVariantMenu();
          }}
        >
          <ListItemIcon>
            <PaletteIcon fontSize="medium" />
          </ListItemIcon>
          Moodboard
        </MenuItem>
      </Menu>
    );

    const handleAddStyle = async (selectedStyle, selectedImgs) => {
      try {
        let styleContent = [];

        for (let i = 0; i < selectedImgs.length; i++) {
          const dimensions = await getImageSize(selectedImgs[i].url);
          styleContent.push({
            id: nanoid(),
            resource_id: selectedStyle.id,
            resource_title: selectedStyle.title,
            resource_owner_id: selectedStyle?.store?.owner_id,
            type: "style",
            img: selectedImgs[i],
            dimensions: dimensions,
            caption: "",
          });
        }

        setContentList([...contentList, ...styleContent]);

        setImportStyleVisible(false);
      } catch (err) {
        toast.error("Failed to add Style image(s)!");
        console.log(err);
      }
    };

    const handleAddMoodboard = async (selectedMoodboards) => {
      try {
        let moodboardContent = [];
        for (let i = 0; i < selectedMoodboards.length; i++) {
          if (selectedMoodboards[i]?.img?.url) {
            const dimensions = await getImageSize(selectedMoodboards[i].img.url);
            moodboardContent.push({
              id: nanoid(),
              resource_id: selectedMoodboards[i].id,
              resource_title: selectedMoodboards[i]?.name,
              resource_owner_id: selectedMoodboards[i]?.owner?.id,
              type: "moodboard",
              img: selectedMoodboards[i].img,
              dimensions: dimensions,

              caption: "",
            });
          }
        }

        setContentList([...contentList, ...moodboardContent]);
        setImportMoodboardVisible(false);
      } catch (err) {
        toast.error("Failed to add Moodboard image(s)!");
        console.log(err);
      }
    };

    const handleOnFileChange = async (e) => {
      const prefileList = e?.target?.files;
      const fileList = uploadFileSizeLimit(prefileList);
      if (uploadRef?.current) {
        uploadRef.current.value = "";
      }
      if (fileList.length == 0) {
        return;
      }
      setUploading(true);
      try {
        const { data } = await api.uploads.create(fileList, {}, `/post/${nanoid()}`);
        const results = data?.uploads || [];
        const addContents = [];
        for (let i = 0; i < results.length; i++) {
          let mimeType = "image/*";
          for (let j = 0; j < fileList.length; j++) {
            if (fileList[j].name == results[i].name) {
              mimeType = fileList[j].type;
            }
          }

          const dimensions = await getImageSize(results[i].url);

          let content = {
            id: nanoid(),
            type: "image",
            img: {
              url: results[i].url,
              type: mimeType,
              name: results[i].name,
            },
            dimensions: dimensions,
            caption: "",
          };
          addContents.push(content);
        }
        setContentList([...contentList, ...addContents]);

        toast.success("Upload successfully!");
        setUploading(false);
      } catch (err) {
        console.log(err);
        toast.error("Upload failed!");

        setUploading(false);
      }
    };

    const handleDelete = (index) => {
      let newContentList = [...contentList];
      newContentList.splice(index, 1);
      setContentList(newContentList);
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth={true}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        sx={{ minWidth: 800 }}
      >
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {"Edit Grid"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div style={{ overflow: "auto", maxHeight: "600px" }}>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <SuiBox sx={{ px: 2 }}>
                <SuiBox sx={{ width: "100%" }}>
                  <SuiBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                    <SuiBox
                      display="flex"
                      alignItems="center"
                      gap={2}
                      sx={{ mb: 2, pt: 1, flexGrow: 1 }}
                    >
                      <SuiButton
                        variant="outlined"
                        color="dark"
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          borderRadius: 1,
                          px: 1,
                          fontSize: 12,
                          minWidth: 120,
                          py: 0,
                          whiteSpace: "nowrap",
                        }}
                        onClick={(e) => setGridVariantAnchor(e.target)}
                      >
                        <AddIcon sx={{ width: 20, height: 20 }} />
                        Add Images
                        <ArrowDropDownIcon sx={{ width: 20, height: 20 }} />
                      </SuiButton>
                      <SuiBox
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          minWidth: 160,
                        }}
                      >
                        <SuiTypography
                          variant="body1"
                          color="dark"
                          sx={{ minWidth: 24, width: 24 }}
                        >
                          Cols
                        </SuiTypography>
                        <Slider
                          valueLabelDisplay="auto"
                          min={1}
                          max={contentList?.length || 1}
                          value={gridSetting?.col || 1}
                          marks
                          onChange={(e, value) =>
                            gridSetting.col != value
                              ? setGridSetting((prev) => ({ ...prev, col: value }))
                              : null
                          }
                          sx={{ flexGrow: 1 }}
                        />
                        <SuiBox sx={{ width: 50, minWidth: 50 }}>
                          <SuiInput value={gridSetting?.col || 1} readOnly disabled type="number" />{" "}
                        </SuiBox>
                      </SuiBox>
                      <SuiBox
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          width: 200,
                          minWidth: 200,
                        }}
                      >
                        <SuiTypography
                          variant="body1"
                          color="dark"
                          sx={{ minWidth: 24, width: 24 }}
                        >
                          Gap
                        </SuiTypography>
                        <Slider
                          valueLabelDisplay="auto"
                          min={0}
                          max={80}
                          value={gridSetting?.gap || 0}
                          onChange={(e, value) =>
                            setGridSetting((prev) => ({ ...prev, gap: value }))
                          }
                          sx={{ flexGrow: 1 }}
                        />
                        <SuiBox sx={{ width: 50, minWidth: 50 }}>
                          <SuiInput value={gridSetting?.gap || 0} readOnly disabled type="number" />{" "}
                        </SuiBox>
                      </SuiBox>
                    </SuiBox>
                    <SuiBox display={"flex"} alignItems={"center"}>
                      <SuiButton variant="text" size="medium" onClick={handleClose} color="dark">
                        Cancel
                      </SuiButton>

                      <SuiButton
                        size="medium"
                        variant="gradient"
                        color="dark"
                        onClick={handleSaveNewOrder}
                      >
                        Save
                      </SuiButton>
                    </SuiBox>
                  </SuiBox>
                  {contentList && contentList.length > 0 ? (
                    <SortableList onSortEnd={handleSortEnd} distance={10}>
                      <RenderGridContentList
                        list={contentList}
                        col={gridSetting?.col}
                        gap={gridSetting?.gap}
                        sortable={true}
                        handleDelete={handleDelete}
                      />
                    </SortableList>
                  ) : null}
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
          <GridVariantMenu />
          <input
            type="file"
            id="imgupload"
            style={{ display: "none" }}
            ref={uploadRef}
            accept="image/*"
            onChange={handleOnFileChange}
            multiple
          />
          {importStyleVisible ? (
            <ImportStylePicturesModal
              handleClose={() => setImportStyleVisible(false)}
              visible={importStyleVisible}
              handleOk={handleAddStyle}
            />
          ) : null}
          {importMoodboardVisible ? (
            <ImportMoodboardPicture
              handleClose={() => setImportMoodboardVisible(false)}
              visible={importMoodboardVisible}
              handleOk={handleAddMoodboard}
              selectedMoodboards={[]}
              isMulti={true}
            />
          ) : null}
        </div>
      </Dialog>
    );
  }
);

export default EditPhotoGrid;
